import { FeedbackCard, FeedbackRating } from "@beydesign/storybook"
import React, { useState } from "react"
import { useAppContext } from "../../app/Context"
import { useMixpanel } from "../../app/hooks/useMixpanel"
import { AnalyticsEvents } from "../../types/Analytics"
import { FeedbackContext } from "../../types/Feedback"
import { getStoredCallId } from "../../utils/general"

const feedbackTopics = [
  "Audio quality",
  "Avatar quality",
  "Responses",
  "Interface",
  "Chat",
]

interface FeedbackProps {
  visible: boolean
  onClose: () => void
  onOpen: () => void
  onSubmit: () => void
}

export default function Feedback({
  visible,
  onClose,
  onOpen,
  onSubmit,
}: FeedbackProps) {
  const [rating, setRating] = useState<FeedbackRating | null>(null)
  const [feedback, setFeedback] = useState<string>("")
  const [contactConsent, setContactConsent] = useState<boolean>(false)
  const [feedbackTopic, setFeedbackTopic] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const context = useAppContext()
  const { logAnalyticsEvent } = useMixpanel()

  const handleClearFeedback = () => {
    setFeedback("")
    setRating(null)
    setContactConsent(false)
    setFeedbackTopic(null)
  }

  const handleSubmit = () => {
    if (!visible) {
      onOpen()
      return
    }

    if (!rating) {
      setError("Please rate the product")
      return
    }

    setError(null)
    logAnalyticsEvent(AnalyticsEvents.Feedback, {
      context: FeedbackContext.AGENT_CALL_END,
      call_id: context.call?.id || getStoredCallId() || undefined,
      rating,
      feedback,
      feedback_topic: feedbackTopic ?? "",
      contact_consent: contactConsent,
    })
    handleClearFeedback()
    onClose()
    onSubmit()
  }

  const handleRatingChange = (nextRating: FeedbackRating | null) => {
    setRating(nextRating)
    if (nextRating && !visible) {
      onOpen()
    }
  }

  return (
    <FeedbackCard
      rating={rating}
      onRatingChange={handleRatingChange}
      expanded={visible}
      topics={feedbackTopics}
      selectedTopic={feedbackTopic}
      onTopicChange={setFeedbackTopic}
      comment={feedback}
      onCommentChange={setFeedback}
      contactConsent={contactConsent}
      onContactConsentChange={setContactConsent}
      submitText="Submit Feedback"
      submitDisabled={!rating}
      onSubmit={handleSubmit}
      submitId="call-postscreen-submit-feedback"
      errorText={error}
      surfaceColor="var(--call-feedback-bg)"
      itemColor="var(--call-feedback-item-bg)"
    />
  )
}
